import { property, photos } from "../data/property";
import { ArrowDown, MapPin } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Fireworks from "./Fireworks";
import EnergyLabelBadge from "./EnergyLabelBadge";
import SunFlare from "./SunFlare";

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  // Subtle parallax: background drifts slower than scroll, content fades / shifts up
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const bgScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);
  const contentY = useTransform(scrollY, [0, 600], [0, -50]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0.4]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={ref}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      {/* Background image with parallax */}
      <motion.img
        src={photos[0]}
        alt="Vooraanzicht Kempenaar 29, Wijk bij Duurstede"
        className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
        loading="eager"
        style={{ y: bgY, scale: bgScale }}
      />
      {/* Sunlight overlay */}
      <SunFlare intensity="medium" side="top-right" />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

      {/* Fireworks overlay — auto-stops on scroll */}
      <Fireworks />

      {/* Content */}
      <motion.div
        className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-between px-6 pt-32 pb-16 md:px-12 lg:px-20"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        {/* Top tag */}
        <motion.div
          className="flex flex-wrap items-center gap-4 text-white/90"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-white/60" />
            <span className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>
              Nieuw te koop · {property.status}
            </span>
          </div>
          <EnergyLabelBadge label={property.energyLabel} size="sm" />
        </motion.div>

        {/* Main content */}
        <div className="max-w-4xl text-white">
          <motion.h1
            data-testid="hero-title"
            className="font-serif text-5xl leading-[0.95] tracking-tight text-balance sm:text-7xl lg:text-[8rem]"
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {property.address}
          </motion.h1>
          <motion.p
            className="mt-6 flex items-center gap-3 font-sans text-lg text-white/90 sm:text-xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          >
            <MapPin size={18} className="text-white/70" />
            {property.postalCode} {property.city} · {property.neighborhood}
          </motion.p>

          <motion.p
            className="mt-8 max-w-2xl font-serif text-xl italic text-white/90 sm:text-2xl text-balance"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          >
            "{property.tagline}"
          </motion.p>

          {/* Price + CTAs */}
          <motion.div
            className="mt-12 flex flex-col items-start gap-8 sm:flex-row sm:items-end sm:gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <div>
              <p className="overline" style={{ color: "rgba(255,255,255,0.75)" }}>
                Vraagprijs
              </p>
              <p
                data-testid="hero-price"
                className="mt-2 font-serif text-4xl sm:text-5xl"
              >
                {property.price}{" "}
                <span className="font-sans text-base font-normal text-white/70">
                  {property.priceSuffix}
                </span>
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="hero-cta-bezichtiging"
                onClick={() => { trackEvent("cta_click", { location: "hero", action: "viewing_request" }); scrollTo("contact"); }}
                className="inline-flex items-center justify-center bg-white px-8 py-4 text-sm font-medium tracking-wide text-brand-ink transition-all hover:bg-brand-bg"
              >
                Plan een bezichtiging
              </button>
              <button
                data-testid="hero-cta-galerij"
                onClick={() => { trackEvent("cta_click", { location: "hero", action: "view_gallery" }); scrollTo("galerij"); }}
                className="inline-flex items-center justify-center border border-white/60 px-8 py-4 text-sm font-medium tracking-wide text-white transition-all hover:bg-white hover:text-brand-ink"
              >
                Bekijk woning
              </button>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="mt-12 flex items-center justify-between text-white/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.6 }}
        >
          <button
            data-testid="hero-scroll-down"
            onClick={() => scrollTo("verhaal")}
            className="flex items-center gap-3 text-xs font-medium tracking-[0.2em] uppercase hover:text-white"
          >
            Scroll om te ontdekken
            <ArrowDown size={14} className="animate-bounce" style={{ animationDuration: "2.4s" }} />
          </button>
          <p className="hidden text-xs tracking-wider text-white/60 md:block">
            68 foto's · 360° tour · plattegronden
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
